import { api, ApiError } from './client.js';
import { sportMeta } from '../content/sportMeta.js';

/*
 * Unauthenticated endpoints — readable by logged-out visitors.
 *
 * GET /api/public/accuracy, real payload:
 *
 *   {
 *     ok, source: "top_picks",
 *     last_30_days: { graded, correct, incorrect, void, accuracy_pct },
 *     all_time:     { … same … },
 *     trend:    [{ date: "2026-08-02", graded, correct, accuracy_pct }, …30],
 *     by_sport: [{ sport: "rugby", graded, correct, accuracy_pct }, …],
 *     cache_ttl_seconds, cached
 *   }
 *
 * Two things to know about the extra series:
 *   - `trend` has one bucket per day with no gaps, but `accuracy_pct` is null
 *     on days where nothing was graded. Null is "no data", NOT zero percent —
 *     plotting it as 0 would invent a run of terrible days.
 *   - `by_sport` is always all-time, whichever window the caller is showing.
 */

export const PUBLIC_ACCURACY_PATH = '/api/public/accuracy';

/**
 * Minimum graded picks before a percentage is published.
 *
 * Below this the sample is too small for the figure to mean anything, so the
 * marketing page and the track record show a "building our track record" state
 * with the graded count and no percentage. This is the single place the floor
 * is defined — change it here and every surface follows.
 */
export const MIN_GRADED_FOR_PUBLIC_ACCURACY = 30;

/**
 * Minimum graded picks before a single sport's percentage is published.
 *
 * Same reasoning as the site-wide floor, applied per row: "MMA 100%" off five
 * picks is not a track record. Rows under this show their graded count instead
 * of a percentage. Set to 0 to publish every row.
 */
export const MIN_GRADED_PER_SPORT = 10;

/**
 * Minimum days with data before the trend chart is drawn.
 *
 * The series is 30 buckets wide but often only a few carry data. Joining three
 * scattered points into a line would read as a trend that isn't there.
 */
export const MIN_TREND_POINTS = 8;

/**
 * Resolves to null — rather than throwing — when the endpoint is unreachable,
 * so a backend blip is an empty state rather than an error screen. We never
 * substitute an invented figure.
 */
export async function getPublicAccuracy({ signal } = {}) {
  try {
    return await api.get(PUBLIC_ACCURACY_PATH, { signal });
  } catch (err) {
    if (err instanceof ApiError) return null;
    throw err;
  }
}

/**
 * Reduce the payload to what the widgets render.
 *
 * @param raw       response from getPublicAccuracy()
 * @param window    'last_30_days' (default) or 'all_time'
 * @returns null when nothing has been graded at all; otherwise an object whose
 *          `meetsFloor` flag says whether the percentage may be shown.
 *
 * `pct` is present regardless, but callers must not display it unless
 * `meetsFloor` is true — below the floor only `graded` may be shown.
 */
export function normaliseAccuracy(raw, window = 'last_30_days') {
  if (!raw) return null;

  const source = raw[window] ?? raw.last_30_days ?? raw.all_time;
  if (!source) return null;

  const graded = Number(source.graded);
  const pct = Number(source.accuracy_pct);

  // With nothing graded there is no figure and no count worth showing.
  if (!Number.isFinite(graded) || graded === 0 || !Number.isFinite(pct)) return null;

  const meetsFloor = graded >= MIN_GRADED_FOR_PUBLIC_ACCURACY;

  return {
    pct: Math.round(pct),
    graded,
    meetsFloor,
    // Correct/incorrect would let anyone derive the percentage, so they are
    // withheld below the floor too.
    correct: meetsFloor ? Number(source.correct) || 0 : null,
    incorrect: meetsFloor ? Number(source.incorrect) || 0 : null,
    windowDays: window === 'all_time' ? null : 30,
    isAllTime: window === 'all_time',
    // Not provided by the endpoint; the widget hides this when absent.
    deltaPts: null,
    trend: normaliseTrend(raw.trend),
  };
}

/**
 * Daily buckets → a plottable series.
 *
 * Days with nothing graded are dropped rather than plotted as zero. That leaves
 * the x-axis unevenly spaced, which is why `hasEnoughPoints` gates the chart:
 * a handful of scattered days is a set of readings, not a trend.
 */
export function normaliseTrend(trend) {
  if (!Array.isArray(trend)) return { points: [], days: [], hasEnoughPoints: false };

  const withData = trend.filter(
    (bucket) => bucket && bucket.accuracy_pct !== null && Number.isFinite(Number(bucket.accuracy_pct))
  );

  return {
    points: withData.map((bucket) => Number(bucket.accuracy_pct)),
    days: withData.map((bucket) => ({
      date: bucket.date,
      graded: Number(bucket.graded) || 0,
      correct: Number(bucket.correct) || 0,
      pct: Number(bucket.accuracy_pct),
    })),
    totalBuckets: trend.length,
    hasEnoughPoints: withData.length >= MIN_TREND_POINTS,
  };
}

/**
 * Per-sport rows, labelled and ordered by sample size.
 *
 * `meetsFloor` per row decides whether that sport's percentage is shown; the
 * graded count is always safe to display.
 */
export function normaliseBySport(bySport) {
  if (!Array.isArray(bySport)) return [];

  return bySport
    .map((row) => {
      const graded = Number(row.graded) || 0;
      const pct = Number(row.accuracy_pct);
      const { name, emoji } = sportMeta(row.sport);

      return {
        key: row.sport,
        name,
        emoji,
        graded,
        correct: Number(row.correct) || 0,
        pct: Number.isFinite(pct) ? Math.round(pct) : null,
        meetsFloor: graded >= MIN_GRADED_PER_SPORT && Number.isFinite(pct),
      };
    })
    .filter((row) => row.graded > 0)
    .sort((a, b) => b.graded - a.graded);
}
